import type { DiscoverySourceId, RecordSourceId } from '../sources';

/**
 * The attribute catalog: every attribute the platform knows how to extract.
 *
 *  - NORMALIZED attributes mean the same thing across sources (name, phone, city, ...).
 *  - PLATFORM attributes are native to one source and namespaced (`google_maps.rating`).
 *  - DERIVED attributes are computed from other values (domain, branch count, hiring).
 *  - CUSTOM attributes (`custom.<slug>`) are defined per research run by the user.
 *
 * Frequently queried attributes map to typed columns on `accounts` (`column`); everything
 * else lives in the account's JSON document and stays filterable through json_extract.
 */
export const ATTRIBUTE_GROUPS = [
  { id: 'identity', label: 'Identity', description: 'Who the business is' },
  { id: 'contact', label: 'Contact', description: 'How to reach them' },
  { id: 'location', label: 'Location', description: 'Where they operate' },
  { id: 'social', label: 'Social', description: 'Social media profiles' },
  { id: 'company', label: 'Company', description: 'Firmographics, hiring and people' },
  { id: 'business', label: 'Business details', description: 'Hours, payments and offerings' },
  { id: 'google_maps', label: 'Google Maps', description: 'Native Google Maps place data' },
  { id: 'linkedin', label: 'LinkedIn', description: 'Native LinkedIn company data' },
  { id: 'yellow_pages', label: 'Yellow Pages & Directories', description: 'Native directory listing data' },
  { id: 'website', label: 'Website signals', description: 'Signals found on the company website' },
  { id: 'meta', label: 'Sources & metadata', description: 'Provenance and data quality' },
] as const;

export type AttributeGroupId = (typeof ATTRIBUTE_GROUPS)[number]['id'];

export type AttributeType =
  | 'text'
  | 'longtext'
  | 'number'
  | 'integer'
  | 'boolean'
  | 'url'
  | 'email'
  | 'phone'
  | 'date'
  | 'list'
  | 'rating'
  | 'coordinates'
  | 'hours'
  | 'image'
  | 'json';

export type AttributeLayer = 'normalized' | 'platform' | 'derived' | 'custom';

/** Typed columns on the accounts table (see src/worker/db/schema.ts). */
export const ACCOUNT_COLUMNS = [
  'name',
  'category',
  'industry',
  'description',
  'website',
  'domain',
  'phone',
  'email',
  'address',
  'area',
  'city',
  'region',
  'country',
  'latitude',
  'longitude',
  'rating',
  'review_count',
  'price_level',
  'employee_range',
  'employee_min',
  'employee_max',
  'employee_count',
  'founded_year',
  'linkedin_url',
  'facebook_url',
  'instagram_url',
  'tiktok_url',
  'twitter_url',
  'youtube_url',
  'logo_url',
  'google_maps_url',
  'is_hiring',
  'job_count',
] as const;

export type AccountColumn = (typeof ACCOUNT_COLUMNS)[number];

export interface AttributeDefinition {
  key: string;
  label: string;
  group: AttributeGroupId;
  type: AttributeType;
  /** Multiple values allowed (phones, emails, categories). */
  many?: boolean;
  description?: string;
  layer: AttributeLayer;
  /** Record sources that can natively provide the attribute. */
  providers: readonly RecordSourceId[];
  /** Typed account column backing the canonical value. */
  column?: AccountColumn;
  /** Selected by default for new company research. */
  defaultOn?: boolean;
  /** Always extracted internally (needed for merging/filtering) even when not displayed. */
  internal?: boolean;
  /** Informational cost hint shown in the attribute picker. */
  costNote?: string;
}

const WEB: RecordSourceId = 'company_website';
const GM: RecordSourceId = 'google_maps';
const LI: RecordSourceId = 'linkedin';
const YP: RecordSourceId = 'yellow_pages';
const AW: RecordSourceId = 'any_website';
const CSV: RecordSourceId = 'csv_import';

const ATMOSPHERE = 'Uses the Places "Enterprise + Atmosphere" billing tier.';

function gm(key: string, label: string, type: AttributeType, extra: Partial<AttributeDefinition> = {}): AttributeDefinition {
  return { key: `google_maps.${key}`, label, group: 'google_maps', type, layer: 'platform', providers: [GM], ...extra };
}

function li(key: string, label: string, type: AttributeType, extra: Partial<AttributeDefinition> = {}): AttributeDefinition {
  return { key: `linkedin.${key}`, label, group: 'linkedin', type, layer: 'platform', providers: [LI], ...extra };
}

function yp(key: string, label: string, type: AttributeType, extra: Partial<AttributeDefinition> = {}): AttributeDefinition {
  return { key: `yellow_pages.${key}`, label, group: 'yellow_pages', type, layer: 'platform', providers: [YP], ...extra };
}

function web(key: string, label: string, type: AttributeType, extra: Partial<AttributeDefinition> = {}): AttributeDefinition {
  return { key: `website.${key}`, label, group: 'website', type, layer: 'platform', providers: [WEB], ...extra };
}

export const ATTRIBUTES: readonly AttributeDefinition[] = [
  // ---- Identity -------------------------------------------------------------------
  { key: 'name', label: 'Name', group: 'identity', type: 'text', layer: 'normalized', providers: [GM, LI, YP, AW, CSV, WEB], column: 'name', defaultOn: true, internal: true },
  { key: 'logo_url', label: 'Logo', group: 'identity', type: 'image', layer: 'normalized', providers: [LI, YP, WEB, AW], column: 'logo_url' },
  { key: 'description', label: 'Description', group: 'identity', type: 'longtext', layer: 'normalized', providers: [GM, LI, YP, AW, CSV, WEB], column: 'description' },
  { key: 'category', label: 'Category', group: 'identity', type: 'text', layer: 'normalized', providers: [GM, YP, AW, CSV], column: 'category', defaultOn: true },
  { key: 'categories', label: 'All categories', group: 'identity', type: 'list', many: true, layer: 'normalized', providers: [GM, YP, AW] },
  { key: 'industry', label: 'Industry', group: 'identity', type: 'text', layer: 'normalized', providers: [LI, CSV, AW], column: 'industry', defaultOn: true },
  { key: 'cuisine', label: 'Cuisine', group: 'identity', type: 'list', many: true, layer: 'derived', providers: [GM, YP], description: 'Derived from restaurant place types and directory categories.' },

  // ---- Contact --------------------------------------------------------------------
  { key: 'phone', label: 'Phone', group: 'contact', type: 'phone', many: true, layer: 'normalized', providers: [GM, YP, AW, CSV, LI, WEB], column: 'phone', defaultOn: true, internal: true },
  { key: 'email', label: 'Email', group: 'contact', type: 'email', many: true, layer: 'normalized', providers: [YP, AW, CSV, WEB], column: 'email', defaultOn: true, internal: true },
  { key: 'website', label: 'Website', group: 'contact', type: 'url', layer: 'normalized', providers: [GM, LI, YP, AW, CSV], column: 'website', defaultOn: true, internal: true },
  { key: 'domain', label: 'Domain', group: 'contact', type: 'text', layer: 'derived', providers: [GM, LI, YP, AW, CSV, WEB], column: 'domain', description: 'Registrable domain of the website, used for matching.' },
  { key: 'whatsapp', label: 'WhatsApp', group: 'contact', type: 'phone', layer: 'normalized', providers: [WEB, YP, AW] },
  { key: 'contact_page_url', label: 'Contact page', group: 'contact', type: 'url', layer: 'normalized', providers: [WEB] },

  // ---- Location -------------------------------------------------------------------
  { key: 'address', label: 'Address', group: 'location', type: 'text', layer: 'normalized', providers: [GM, YP, AW, CSV, LI, WEB], column: 'address', defaultOn: true },
  { key: 'area', label: 'District / area', group: 'location', type: 'text', layer: 'normalized', providers: [GM, YP, AW, CSV], column: 'area', defaultOn: true },
  { key: 'city', label: 'City', group: 'location', type: 'text', layer: 'normalized', providers: [GM, YP, AW, CSV, LI, WEB], column: 'city', defaultOn: true, internal: true },
  { key: 'region', label: 'Region / state', group: 'location', type: 'text', layer: 'normalized', providers: [GM, YP, LI], column: 'region' },
  { key: 'postal_code', label: 'Postal code', group: 'location', type: 'text', layer: 'normalized', providers: [GM, YP, WEB] },
  { key: 'country', label: 'Country', group: 'location', type: 'text', layer: 'normalized', providers: [GM, YP, AW, CSV, LI, WEB], column: 'country', defaultOn: true, internal: true },
  { key: 'coordinates', label: 'Coordinates', group: 'location', type: 'coordinates', layer: 'normalized', providers: [GM, YP, WEB] },
  { key: 'headquarters', label: 'Headquarters', group: 'location', type: 'text', layer: 'normalized', providers: [LI, WEB] },
  { key: 'locations', label: 'Locations / offices', group: 'location', type: 'list', many: true, layer: 'normalized', providers: [LI, WEB] },
  { key: 'branch_count', label: 'Branches found', group: 'location', type: 'integer', layer: 'derived', providers: [GM, YP, LI], description: 'Listings of the same brand found in this dataset (a lower bound, not a census).' },

  // ---- Social ---------------------------------------------------------------------
  { key: 'linkedin_url', label: 'LinkedIn', group: 'social', type: 'url', layer: 'normalized', providers: [LI, WEB, YP, AW, CSV], column: 'linkedin_url', defaultOn: true },
  { key: 'facebook_url', label: 'Facebook', group: 'social', type: 'url', layer: 'normalized', providers: [WEB, YP, AW, GM, CSV], column: 'facebook_url', defaultOn: true },
  { key: 'instagram_url', label: 'Instagram', group: 'social', type: 'url', layer: 'normalized', providers: [WEB, YP, AW, GM, CSV], column: 'instagram_url', defaultOn: true },
  { key: 'tiktok_url', label: 'TikTok', group: 'social', type: 'url', layer: 'normalized', providers: [WEB, AW], column: 'tiktok_url', defaultOn: true },
  { key: 'twitter_url', label: 'X / Twitter', group: 'social', type: 'url', layer: 'normalized', providers: [WEB, YP, AW], column: 'twitter_url', defaultOn: true },
  { key: 'youtube_url', label: 'YouTube', group: 'social', type: 'url', layer: 'normalized', providers: [WEB, AW], column: 'youtube_url' },
  { key: 'snapchat_url', label: 'Snapchat', group: 'social', type: 'url', layer: 'normalized', providers: [WEB, AW] },
  { key: 'pinterest_url', label: 'Pinterest', group: 'social', type: 'url', layer: 'normalized', providers: [WEB] },
  { key: 'telegram_url', label: 'Telegram', group: 'social', type: 'url', layer: 'normalized', providers: [WEB] },

  // ---- Company --------------------------------------------------------------------
  { key: 'employee_range', label: 'Employee range', group: 'company', type: 'text', layer: 'normalized', providers: [LI, CSV], column: 'employee_range' },
  { key: 'employee_count', label: 'Employees on LinkedIn', group: 'company', type: 'integer', layer: 'normalized', providers: [LI], column: 'employee_count' },
  { key: 'founded_year', label: 'Founded', group: 'company', type: 'integer', layer: 'normalized', providers: [LI, YP, WEB], column: 'founded_year' },
  { key: 'specialties', label: 'Specialties', group: 'company', type: 'list', many: true, layer: 'normalized', providers: [LI, WEB] },
  { key: 'company_type', label: 'Company type', group: 'company', type: 'text', layer: 'normalized', providers: [LI] },
  { key: 'is_hiring', label: 'Hiring?', group: 'company', type: 'boolean', layer: 'derived', providers: [LI, WEB], column: 'is_hiring', description: 'Yes when open jobs were found on LinkedIn or the careers page.' },
  { key: 'open_jobs_count', label: 'Open jobs', group: 'company', type: 'integer', layer: 'derived', providers: [LI, WEB], column: 'job_count' },
  { key: 'jobs', label: 'Current jobs', group: 'company', type: 'json', many: true, layer: 'normalized', providers: [LI, WEB] },
  { key: 'people', label: 'People / decision makers', group: 'company', type: 'json', many: true, layer: 'normalized', providers: [LI, WEB], description: 'Links matching people records to the company (enables people discovery).' },

  // ---- Business details -----------------------------------------------------------
  { key: 'opening_hours', label: 'Opening hours', group: 'business', type: 'hours', layer: 'normalized', providers: [GM, YP, WEB] },
  { key: 'payment_methods', label: 'Payment methods', group: 'business', type: 'list', many: true, layer: 'normalized', providers: [GM, YP] },
  { key: 'brands', label: 'Brands carried', group: 'business', type: 'list', many: true, layer: 'normalized', providers: [YP] },

  // ---- Google Maps (platform) --------------------------------------------------------
  gm('rating', 'Rating', 'rating', { column: 'rating', defaultOn: true }),
  gm('review_count', 'Reviews', 'integer', { column: 'review_count', defaultOn: true }),
  gm('price_level', 'Price level', 'integer', { column: 'price_level', description: '1 (inexpensive) – 4 (very expensive).' }),
  gm('price_range', 'Price range', 'text'),
  gm('maps_url', 'Google Maps URL', 'url', { column: 'google_maps_url', defaultOn: true }),
  gm('place_id', 'Place ID', 'text'),
  gm('business_status', 'Business status', 'text'),
  gm('types', 'Place types', 'list', { many: true }),
  gm('photos', 'Photos', 'list', { many: true, costNote: 'Each resolved photo is a billed Place Photos request.' }),
  gm('plus_code', 'Plus code', 'text'),
  gm('reviews', 'Top reviews', 'json', { many: true, costNote: ATMOSPHERE, description: 'Review text and rating only; reviewer names are not stored.' }),
  gm('delivery', 'Delivery', 'boolean', { costNote: ATMOSPHERE }),
  gm('takeout', 'Takeout', 'boolean', { costNote: ATMOSPHERE }),
  gm('dine_in', 'Dine-in', 'boolean', { costNote: ATMOSPHERE }),
  gm('reservable', 'Takes reservations', 'boolean', { costNote: ATMOSPHERE }),
  gm('serves_breakfast', 'Serves breakfast', 'boolean', { costNote: ATMOSPHERE }),
  gm('serves_lunch', 'Serves lunch', 'boolean', { costNote: ATMOSPHERE }),
  gm('serves_dinner', 'Serves dinner', 'boolean', { costNote: ATMOSPHERE }),
  gm('serves_coffee', 'Serves coffee', 'boolean', { costNote: ATMOSPHERE }),
  gm('serves_dessert', 'Serves dessert', 'boolean', { costNote: ATMOSPHERE }),
  gm('serves_vegetarian', 'Vegetarian options', 'boolean', { costNote: ATMOSPHERE }),
  gm('outdoor_seating', 'Outdoor seating', 'boolean', { costNote: ATMOSPHERE }),
  gm('good_for_groups', 'Good for groups', 'boolean', { costNote: ATMOSPHERE }),
  gm('good_for_children', 'Good for children', 'boolean', { costNote: ATMOSPHERE }),
  gm('live_music', 'Live music', 'boolean', { costNote: ATMOSPHERE }),
  gm('wheelchair_accessible', 'Wheelchair accessible entrance', 'boolean'),

  // ---- LinkedIn (platform) -----------------------------------------------------------
  li('follower_count', 'Followers', 'integer'),
  li('tagline', 'Tagline', 'text'),
  li('company_id', 'Company ID', 'text'),
  li('jobs_url', 'Jobs page', 'url'),

  // ---- Yellow Pages / directories (platform) -------------------------------------------
  yp('listing_url', 'Listing URL', 'url'),
  yp('rating', 'Directory rating', 'rating'),
  yp('review_count', 'Directory reviews', 'integer'),
  yp('keywords', 'Keywords', 'list', { many: true }),
  yp('verified', 'Verified listing', 'boolean'),
  yp('directory', 'Directory', 'text'),

  // ---- Website signals ------------------------------------------------------------------
  web('title', 'Homepage title', 'text'),
  web('has_careers_page', 'Has careers page', 'boolean'),
  web('careers_url', 'Careers page', 'url'),
  web('platform', 'Website platform', 'text', { description: 'Detected CMS / builder (WordPress, Shopify, Wix, ...).' }),
  web('tracking_tools', 'Marketing / analytics tools', 'list', { many: true, description: 'Detected tags such as Google Analytics, Meta Pixel, HubSpot.' }),
  web('languages', 'Website languages', 'list', { many: true }),
  web('status', 'Website status', 'text'),

  // ---- Metadata -------------------------------------------------------------------------
  { key: 'meta.sources', label: 'Sources', group: 'meta', type: 'list', many: true, layer: 'derived', providers: [GM, LI, YP, AW, CSV, WEB], defaultOn: true },
  { key: 'meta.source_urls', label: 'Source URLs', group: 'meta', type: 'list', many: true, layer: 'derived', providers: [GM, LI, YP, AW, CSV, WEB], defaultOn: true },
  { key: 'meta.source_count', label: 'Source count', group: 'meta', type: 'integer', layer: 'derived', providers: [GM, LI, YP, AW, CSV, WEB] },
  { key: 'meta.match_confidence', label: 'Merge confidence', group: 'meta', type: 'number', layer: 'derived', providers: [GM, LI, YP, AW, CSV, WEB] },
  { key: 'meta.completeness', label: 'Completeness', group: 'meta', type: 'number', layer: 'derived', providers: [GM, LI, YP, AW, CSV, WEB], description: 'Share of selected attributes that have a value.' },
  { key: 'meta.first_seen', label: 'First seen', group: 'meta', type: 'date', layer: 'derived', providers: [GM, LI, YP, AW, CSV, WEB] },
  { key: 'meta.updated_at', label: 'Last updated', group: 'meta', type: 'date', layer: 'derived', providers: [GM, LI, YP, AW, CSV, WEB] },
];

const BY_KEY = new Map(ATTRIBUTES.map((attribute) => [attribute.key, attribute]));

export function getAttribute(key: string): AttributeDefinition | undefined {
  return BY_KEY.get(key);
}

export function isKnownAttribute(key: string): boolean {
  return BY_KEY.has(key) || key.startsWith('custom.');
}

/** Attribute keys that are always extracted because merging and contact filtering depend on them. */
export const INTERNAL_ATTRIBUTE_KEYS: readonly string[] = ATTRIBUTES.filter((a) => a.internal).map((a) => a.key);

export interface AvailabilityContext {
  sources: readonly DiscoverySourceId[];
  /** Company-website enrichment enabled. */
  websiteEnrichment: boolean;
}

/** Record sources that can contribute data for a research run. */
export function contributingSources(context: AvailabilityContext): Set<RecordSourceId> {
  const set = new Set<RecordSourceId>(context.sources);
  if (context.websiteEnrichment) set.add('company_website');
  return set;
}

/** Attributes the selected sources can plausibly provide, in catalog order. */
export function availableAttributes(context: AvailabilityContext): AttributeDefinition[] {
  const contributing = contributingSources(context);
  return ATTRIBUTES.filter((attribute) => attribute.providers.some((provider) => contributing.has(provider)));
}

export interface AttributeGroupView {
  id: AttributeGroupId;
  label: string;
  description: string;
  attributes: AttributeDefinition[];
}

export function groupAttributes(attributes: readonly AttributeDefinition[]): AttributeGroupView[] {
  return ATTRIBUTE_GROUPS.map((group) => ({
    id: group.id,
    label: group.label,
    description: group.description,
    attributes: attributes.filter((attribute) => attribute.group === group.id),
  })).filter((group) => group.attributes.length > 0);
}

/** Smart default selection for the given sources. */
export function defaultAttributeSelection(context: AvailabilityContext): string[] {
  return availableAttributes(context)
    .filter((attribute) => attribute.defaultOn)
    .map((attribute) => attribute.key);
}

export function attributeLabel(key: string): string {
  return BY_KEY.get(key)?.label ?? key.replace(/^custom\./, '').replace(/_/g, ' ');
}
